<script setup lang="ts">
import {
    faArrowRightToBracket,
    faBadgeCheck,
    faChevronLeft,
    faPen,
    faRepeatAlt,
} from '@fortawesome/pro-regular-svg-icons';
import { UserType } from '@store/auth/types';
import { NuxtLink } from '#components';

const authStore = useAuthStore();
const {
    hasMultipleAccounts,
    levelTitle,
    accountName,
    score,
    userInfo,
    loggedIn,
} = classToRefs(authStore);
const { switchAccount, login } = useAuth();
const isMounted = useMounted();
const levelClass = computed(() => authStore.level || '');
const levelIcon = computed(
    () => `/img/club/level-${authStore.levelIndex + 1}.png`
);
const organizational = computed(
    () => authStore.currentUserType === UserType.Organization
);
const isExtraFeaturesEnabled = computed(
    () => useLayoutStore().isExtraFeaturesEnabled
);
const delayedLoggedIn = computed(() => isMounted.value && loggedIn.value);
</script>

<template>
    <div class="user-info-root">
        <template v-if="userInfo || !delayedLoggedIn">
            <client-only v-if="isExtraFeaturesEnabled">
                <template #fallback>
                    <mr-dots-loading color="white" />
                </template>
                <template v-if="delayedLoggedIn">
                    <div class="user-container">
                        <NuxtLink
                            to="/panel"
                            :class="['user-info', { organizational }]"
                        >
                            <div class="content" :class="{ organizational }">
                                <mr-icon
                                    v-if="organizational"
                                    :icon="faBadgeCheck"
                                    size="x-small"
                                    color="white"
                                />
                                <span class="name">{{ accountName }}</span>
                            </div>
                            <mr-icon :icon="faPen" color="white" />
                        </NuxtLink>

                        <div
                            v-if="hasMultipleAccounts"
                            class="switch-account"
                            @click="switchAccount"
                        >
                            <span>تغییر حساب کاربری</span>
                            <mr-icon size="large" :icon="faRepeatAlt" />
                        </div>
                        <NuxtLink
                            to="/panel/club"
                            class="mr-club"
                            :class="levelClass"
                        >
                            <div class="item">
                                <img
                                    :src="levelIcon"
                                    alt="level"
                                    class="level"
                                />
                                <span class="name">مِستربلیط‌کلاب</span>
                                <mr-button
                                    aria-label="open panel club"
                                    :icon-button="{
                                        icon: faChevronLeft,
                                        color: 'black',
                                    }"
                                    size="small"
                                    variant="transparent"
                                />
                            </div>
                            <div class="details">
                                سطح {{ levelTitle }}
                                <div class="separator">|</div>
                                {{ score }} امتیاز
                            </div>
                        </NuxtLink>
                    </div>
                </template>
                <div v-else class="login-item" @click="() => login()">
                    <mr-icon :icon="faArrowRightToBracket" color="black" />
                    ورود به حساب کاربری
                </div>
            </client-only>
        </template>
        <mr-dots-loading v-else color="white" />
    </div>
</template>

<style scoped lang="scss">
.switch-account {
    display: flex;
    align-items: center;
    justify-content: space-between;
}
.user-info-root {
    background-color: white;
    width: 100%;
    padding-inline: 12px;
    display: flex;
    flex-direction: column;

    .user-container {
        display: flex;
        flex-direction: column;
        gap: 8px;
        padding: 0 12px;
        .user-info {
            margin-block-start: 0;
            margin: 0px -12px !important;
            border-radius: 8px;
            padding: 16px 20px;
            display: flex;
            align-items: center;
            justify-content: space-between;
            border-bottom-left-radius: 0;
            border-bottom-right-radius: 0;
            background-color: usePalette($p-blue);
            &:hover,
            &:active {
                text-decoration: none;
            }

            &.organizational {
                background-color: usePalette($p-green) !important;
            }
            .content {
                display: flex;
                align-items: center;
                gap: 8px;
                .name {
                    @include tg-m($w7);
                    color: white !important;
                }

                &.organizational {
                    .name {
                        @include tg-m($w7);
                        color: white !important;
                    }
                    .details {
                        @include tg-s($color: white);
                    }
                }
            }
        }
        .mr-club {
            margin-bottom: 8px;
            @include mobile-only {
                margin-bottom: 20px;
            }
            border-radius: 8px;
            padding: 4px 8px 4px 0;
            background-color: usePalette($p-yellow, $p-100);
            display: flex;
            flex-direction: column;
            &.Level4 {
                background-color: usePalette($p-cyan, $p-100);
            }
            &.Level3 {
                background-color: usePalette($p-yellow, $p-100);
            }
            &.Level2 {
                background-color: usePalette($p-cool-gray, $p-100);
            }
            &.Level1 {
                background-color: #ffede2;
            }
            &:hover,
            &:active {
                text-decoration: none;
            }
            .item {
                display: flex;
                align-items: center;
                .name {
                    @include tg-m($w7);
                    flex-grow: 1;
                }
                .level {
                    max-height: 28px;
                    margin-left: 8px;
                }
            }
            .details {
                display: flex;
                align-items: center;
                @include tg-s;
                .separator {
                    color: usePalette($p-gray, $p-600);
                    margin-inline: 8px;
                }
            }
        }
    }

    .login-item {
        display: flex;
        align-items: center;
        gap: 12px;
        margin-block: 20px;
        @include tg-m($w5);
    }
    .divider {
        margin-bottom: 20px;
    }
    padding: 0;
    .score-container {
        display: flex;
        flex-direction: column;
        .name {
            @include tg-xl($w7, white);
        }
        .scores {
            display: flex;
            align-items: center;
            justify-content: space-between;
            .details {
                @include tg-s($w7, white);
            }
        }
    }
}
</style>
